import React from "react";
import { Head } from "@inertiajs/react";

export interface SEOProps {
  /**
   * Page title (will override default "Learntokiz 2.0")
   */
  title?: string;
  /**
   * Page description (will override default description)
   */
  description?: string;
  /**
   * OpenGraph/Twitter image URL (will override default image)
   * Should be an absolute URL or path relative to site root
   */
  image?: string;
  /**
   * Page URL (will override default current URL)
   * Should be an absolute URL
   */
  url?: string;
  /**
   * OpenGraph type (defaults to "website")
   */
  type?: "website" | "article" | "video" | "profile" | "book" | "music" | "product";
  /**
   * Twitter username (e.g., "@learntokiz")
   */
  twitterSite?: string;
  /**
   * Additional meta tags (for custom needs)
   */
  meta?: Array<{
    name?: string;
    property?: string;
    content: string;
  }>;
}

/**
 * SEO Component - Reusable SEO meta tag component for pages
 *
 * Uses Inertia's Head component to manage meta tags.
 * Overrides default OpenGraph and Twitter Card meta tags set in application.html.erb.
 *
 * Features:
 * - OpenGraph meta tags (og:title, og:description, og:image, og:url, og:type)
 * - Twitter Card meta tags (twitter:card, twitter:title, twitter:description, twitter:image)
 * - Page title override
 * - Optional custom meta tags
 *
 * @example
 * // Simple usage with just title and description
 * <SEO
 *   title="Videos - Learntokiz 2.0"
 *   description="Browse our collection of urbankiz dance videos"
 * />
 *
 * @example
 * // Full usage with custom image and URL
 * <SEO
 *   title={`${term.name} - Dance Dictionary`}
 *   description={term.definition}
 *   image={term.imageUrl || undefined}
 *   url={`https://learntokiz.com/dictionary/${term.slug}`}
 *   type="article"
 * />
 */
export function SEO({
  title,
  description,
  image,
  url,
  type = "website",
  twitterSite = "@learntokiz",
  meta = [],
}: SEOProps) {
  // SSR-safe origin lookup. When rendered server-side (Inertia SSR),
  // `window` is undefined — fall back to an empty string so URLs render
  // as bare paths (which Rails will resolve against the current host).
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const getAbsoluteUrl = (path: string): string => {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    if (path.startsWith("/")) {
      return `${origin}${path}`;
    }
    return `${origin}/${path}`;
  };

  const currentUrl =
    url ||
    (typeof window !== "undefined" ? window.location.href : "");

  const absoluteImageUrl = image
    ? getAbsoluteUrl(image)
    : `${origin}/images/og-image.png`;

  return (
    <Head>
      {/* Page Title */}
      {title && <title>{title}</title>}

      {/* Description Meta Tag */}
      {description && <meta name="description" content={description} />}

      {/* OpenGraph Meta Tags */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={type} />

      {/* Twitter Card Meta Tags */}
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={absoluteImageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}

      {/* Custom Meta Tags */}
      {meta.map((tag, index) => (
        <meta
          key={index}
          {...(tag.name && { name: tag.name })}
          {...(tag.property && { property: tag.property })}
          content={tag.content}
        />
      ))}
    </Head>
  );
}

export default SEO;

